import React, { useCallback, useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import {
  ChannelStatusFragment,
  ChannelStatusAction as ChannelStatusAction,
  useGetClinicChannelStatusesQuery,
  useGetClinicChannelPetParentsQuery,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import LoadingIndicator from 'shared/components/LoadingIndicator';
import { Link as ReactRouterLink } from 'react-router-dom';
import { RouteBasePaths } from 'routes';
import { Box, Center, Square, Link, CMenuItem, MenuGroup, TextInput, Icon } from '@televet/kibble-ui';
import uniq from 'lodash-es/uniq';
import useGetExistingChannels from 'shared/hooks/useGetExistingChannels';
import { channelStatusActionLabels } from 'shared/components/ConversationStatusSelect';

interface IChannelStatusMenulistProps {
  channelId?: string;
  canReopenChannel: boolean;
  onMenuClose: () => void;
  handleChannelStatusSelect: (channelStatus: ChannelStatusFragment) => void;
  onSetCanReopenChannel: React.Dispatch<React.SetStateAction<boolean>>;
}

const ChannelStatusMenuList = ({
  channelId,
  onMenuClose,
  canReopenChannel,
  handleChannelStatusSelect,
  onSetCanReopenChannel,
}: IChannelStatusMenulistProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();

  const [searchText, setSearchText] = useState('');
  const [existingActiveChannelId, setExistingActiveChannelId] = useState('');

  const searchTextInputRef = useRef<HTMLInputElement | null>(null);

  const { data: channelStatusesData, loading: isChannelStatusesDataLoading } = useGetClinicChannelStatusesQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    onCompleted: () => {
      setTimeout(() => {
        searchTextInputRef.current?.focus();
      }, 0);
    },
  });

  const { data: channelPetParentData } = useGetClinicChannelPetParentsQuery({
    variables: {
      where: {
        id: channelId,
      },
    },
    skip: !channelId,
  });

  const getExistingChannels = useGetExistingChannels();

  const clinicPetParentIds = useMemo(() => {
    const petParentIds = channelPetParentData?.findUniqueChannel?.channelMembers?.flatMap((member) =>
      member.clinicPetParent?.id ? [member.clinicPetParent.id] : [],
    );
    return uniq(petParentIds);
  }, [channelPetParentData?.findUniqueChannel]);

  const filteredStatuses = useMemo(() => {
    if (!channelStatusesData) {
      return [];
    }
    const { findManyChannelStatus: channelStatuses } = channelStatusesData;
    if (!channelStatuses?.length) {
      return [];
    }
    if (!searchText) {
      return channelStatuses;
    }
    return (channelStatuses ?? []).filter((channelStatus) =>
      channelStatus.name.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [searchText, channelStatusesData]);

  const channelStatusOptions = useMemo(() => {
    const defaultOptions = {
      [ChannelStatusAction.Active]: [] as ChannelStatusFragment[],
      [ChannelStatusAction.Inactive]: [] as ChannelStatusFragment[],
      [ChannelStatusAction.InactivePermanently]: [] as ChannelStatusFragment[],
      [ChannelStatusAction.Migrated]: [] as ChannelStatusFragment[],
    };

    if (!filteredStatuses.length) {
      return defaultOptions;
    }

    return filteredStatuses.reduce((prev, curr) => {
      prev[curr.channelStatusAction].push(curr);
      return prev;
    }, defaultOptions);
  }, [filteredStatuses]);

  const handleMenuItemClick = useCallback(
    (e, channelStatus) => {
      e.stopPropagation();
      handleChannelStatusSelect(channelStatus);
    },
    [handleChannelStatusSelect],
  );

  const renderMenuItem = useCallback(
    (channelStatus: ChannelStatusFragment) => {
      const { id, color, name } = channelStatus;
      return (
        <CMenuItem
          data-testid={'status-select-item-' + name}
          key={id}
          onClick={(e): void => handleMenuItemClick(e, channelStatus)}
        >
          <Square bgColor={color} size="1em" borderRadius={4} mr={2} />
          {name}
        </CMenuItem>
      );
    },
    [handleMenuItemClick],
  );

  useEffect(() => {
    if (!clinicPetParentIds || clinicPetParentIds.length === 0) {
      return;
    }
    getExistingChannels({ clinicPetParentIds }).then((ids) => {
      const otherChannelIds = ids.filter((id) => id !== channelId) as string[];
      if (otherChannelIds.length) {
        onSetCanReopenChannel(false);
        setExistingActiveChannelId(otherChannelIds[0]);
      } else {
        onSetCanReopenChannel(true);
        setExistingActiveChannelId('');
      }
    });
  }, [channelId, clinicPetParentIds, getExistingChannels, onSetCanReopenChannel]);
  if (isChannelStatusesDataLoading) {
    return (
      <Center p={5}>
        <LoadingIndicator />
      </Center>
    );
  }

  const ReopenChannel = (): JSX.Element => (
    <Center px={4} pt={1} pb={2}>
      <div>
        {
          'This client already has an active conversation with your team. To open this one instead, you can close or archive '
        }
        <Link
          as={ReactRouterLink}
          to={`${RouteBasePaths.Conversations}/${existingActiveChannelId}`}
          onClick={(event: MouseEvent): void => {
            event.stopPropagation();
            onMenuClose();
          }}
        >
          the existing conversation
        </Link>
        .
      </div>
    </Center>
  );

  return (
    <>
      <Box px={2} pb={1}>
        <TextInput
          ref={searchTextInputRef}
          name="search"
          placeholder="Search..."
          value={searchText}
          onChange={(e): void => setSearchText(e.target.value)}
          onKeyDown={(e): void => {
            if (!['ArrowUp', 'ArrowDown', 'Escape'].includes(e.key)) {
              e.stopPropagation();
            }
          }}
          leftElement={<Icon name="magnifyingGlass" size="md" />}
          isClearable
          onClear={(): void => setSearchText('')}
          onClick={(e): void => e.stopPropagation()}
        />
      </Box>
      {filteredStatuses.length ? (
        <>
          {!!channelStatusOptions[ChannelStatusAction.Active].length && (
            <MenuGroup title={channelStatusActionLabels.Active}>
              {canReopenChannel ? (
                channelStatusOptions[ChannelStatusAction.Active].map(renderMenuItem)
              ) : (
                <ReopenChannel />
              )}
            </MenuGroup>
          )}

          {!!channelStatusOptions[ChannelStatusAction.Inactive].length && (
            <MenuGroup title={channelStatusActionLabels.Inactive}>
              {channelStatusOptions[ChannelStatusAction.Inactive].map(renderMenuItem)}
            </MenuGroup>
          )}

          {!!channelStatusOptions[ChannelStatusAction.InactivePermanently].length && (
            <MenuGroup title={channelStatusActionLabels.InactivePermanently}>
              {channelStatusOptions[ChannelStatusAction.InactivePermanently].map(renderMenuItem)}
            </MenuGroup>
          )}
        </>
      ) : (
        <Center py="4">No results found</Center>
      )}
    </>
  );
};

export default ChannelStatusMenuList;
